/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Box,
  Container,
  Flex,
  IntrinsicImage,
  Link,
  Section,
  Text,
} from '@tishman/components';
import React from 'react';

import { BorderedBox } from '../../components/BorderedBox';

import type { IGatsbyImageData } from 'gatsby-plugin-image';

interface FeaturedStory {
  excerpt: string;
  publishAt: string;
  poster: {
    alt: string;
    asset: {
      gatsbyImageData: IGatsbyImageData;
    };
  };
  titleAndSlug: {
    title: string;
    slug: {
      current: string;
    };
  };
}

interface FeaturedArticle {
  featuredStory: FeaturedStory;
  subtitle: string;
  title: string;
}

const FeaturedArticle = ({
  featuredStory,
  subtitle,
  title,
}: FeaturedArticle): React.JSX.Element => {
  // FORMATTED FEATURED STORY PUBLISH DATE
  const date = new Date(featuredStory?.publishAt ?? new Date());
  const formattedDate = date
    .toLocaleDateString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\//g, '.');

  return (
    <Section theme="Rock Center">
      <Container paddingBottom={[5, 7]} paddingTop={[5, 9]}>
        {/* TITLE & SUBTITLE */}
        <Box
          sx={{
            maxWidth: 730,
            mx: 'auto',
          }}
        >
          <Text
            sx={{
              fontSize: [5, 7],
              fontWeight: 'medium',
              lineHeight: ['34px', '52px'],
              textAlign: 'center',
              variant: 'heading',
            }}
          >
            {title}
          </Text>
          <Text
            sx={{
              fontSize: '16px',
              pb: [3, 6],
              pt: 3,
              textAlign: 'center',
            }}
            variant="mediumP"
          >
            {subtitle}
          </Text>
        </Box>
        {/* FEATURED STORY */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: ['1fr', '1fr 1fr'],
            justifyContent: 'space-between',
            gap: 0,
          }}
        >
          <Box sx={{ maxWidth: 451, pt: [5, 0] }}>
            {/* PUBLISH DATE */}
            <Flex
              marginBottom={2}
              sx={{
                justifyContent: ['space-between', 'flex-start'],
              }}
            >
              <Text
                sx={{ fontSize: 1, fontWeight: 'medium' }}
                variant="mediumP"
              >
                Featured Story
              </Text>
              <Text
                sx={{ fontSize: 1, fontWeight: 'medium', ml: '24px' }}
                variant="mediumP"
              >
                {formattedDate}
              </Text>
            </Flex>
            {/* FEATURED STORY TITLE */}
            <Text
              sx={{
                fontSize: [6, 7],
                fontWeight: 'medium',
                lineHeight: ['34px', '52px'],
                pb: [0, 3],
                variant: 'heading',
              }}
            >
              {featuredStory?.titleAndSlug?.title}
            </Text>
            {/* MOBILE IMAGE */}
            <Box sx={{ display: ['block', 'none'], my: -2 }}>
              <BorderedBox
                sx={{
                  'alignSelf': 'flex-start',
                  'cursor': 'pointer',
                  'overflow': 'initial',
                  '&:hover img': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <IntrinsicImage
                  alt={featuredStory?.poster?.alt}
                  image={featuredStory?.poster?.asset?.gatsbyImageData}
                  ratio={491 / 318}
                />
              </BorderedBox>
            </Box>
            {/* FEATURED STORY EXCERPT */}
            <Text sx={{ fontSize: '16px', pb: [3, 4] }} variant="mediumP">
              {featuredStory?.excerpt}
            </Text>
            <Link
              sx={{ mt: 4 }}
              to={
                `/magazine/arts-culture/${featuredStory?.titleAndSlug?.slug?.current}` ??
                '/magazine'
              }
              variant="underline"
            >
              Read Story
            </Link>
          </Box>
          {/* DESKTOP IMAGE */}
          <Box sx={{ display: ['none', 'block'] }}>
            <BorderedBox
              sx={{
                'alignSelf': 'flex-start',
                'cursor': 'pointer',
                'overflow': 'initial',
                '&:hover img': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <IntrinsicImage
                alt={featuredStory?.poster?.alt}
                image={featuredStory?.poster?.asset?.gatsbyImageData}
                ratio={491 / 318}
              />
            </BorderedBox>
          </Box>
        </Box>
      </Container>
      {/* LINK TO ALL STORIES */}
      <Flex sx={{ justifyContent: 'center', pb: [5, 9] }}>
        <Link
          sx={{
            display: 'inline-block',
            mx: [3, 0],
            textAlign: 'center',
            width: ['100%', 'initial'],
          }}
          to="/magazine"
          variant="buttonBorder"
        >
          Read the stories
        </Link>
      </Flex>
    </Section>
  );
};

export default FeaturedArticle;
